import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, CheckCircle2, Loader2 } from "lucide-react";
import { useLang } from "@/i18n";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIMES = Array.from({ length: 12 }, (_, i) => {
  const h = 13 + Math.floor(i / 2);
  const m = i % 2 === 0 ? "00" : "30";
  return `${String(h).padStart(2, "0")}:${m}`;
});

const fmtTime = (hm) => {
  const [h, m] = hm.split(":").map(Number);
  const h12 = h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, "0")} PM`;
};

const etNow = () => new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }));
const dateKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const ScheduleCall = ({ consultationId }) => {
  const { t } = useLang();
  const s = t.schedule;
  const [booked, setBooked] = useState([]);
  const [dayIdx, setDayIdx] = useState(0);
  const [time, setTime] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState("");
  const [skipped, setSkipped] = useState(false);

  const days = useMemo(() => {
    const out = [];
    const d = etNow();
    while (out.length < 14) {
      if (d.getDay() !== 0) out.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    return out;
  }, []);

  const loadBooked = () =>
    axios.get(`${API}/bookings/booked`).then((r) => Array.isArray(r.data) && setBooked(r.data)).catch(() => {});
  useEffect(() => { loadBooked(); }, []);

  const day = days[dayIdx];
  const slots = useMemo(() => {
    const key = dateKey(day);
    const now = etNow();
    const isToday = key === dateKey(now);
    const nowHm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    return TIMES.map((hm) => ({
      hm,
      disabled: booked.includes(`${key} ${hm}`) || (isToday && hm <= nowHm),
    }));
  }, [day, booked]);

  const allGone = slots.every((x) => x.disabled);

  const confirm = async () => {
    if (!time) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, {
        consultation_id: consultationId,
        slot: `${dateKey(day)} ${time}`,
      });
      setConfirmed(data.pretty);
    } catch (err) {
      toast.error(err.response?.data?.detail || s.toastErr);
      loadBooked();
      setTime("");
    } finally {
      setSubmitting(false);
    }
  };

  if (skipped) return null;

  if (confirmed) {
    return (
      <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center" data-testid="booking-confirmed">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
          <CheckCircle2 size={26} aria-hidden />
        </span>
        <p className="mt-3 font-display text-xl font-semibold text-navy-900">{s.bookedTitle}</p>
        <p className="mt-1.5 text-charcoal-700">{s.bookedBody} <strong>{confirmed}</strong>.</p>
      </div>
    );
  }

  return (
    <div className="mt-8 rounded-2xl border border-navy-100 bg-[#FAFAFC] p-6" data-testid="schedule-call">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gold-500/15 text-gold-600">
          <CalendarCheck size={20} aria-hidden />
        </span>
        <div>
          <p className="font-display text-lg font-semibold text-navy-900">{s.title}</p>
          <p className="mt-1 text-sm text-charcoal-700/80">{s.sub}</p>
        </div>
      </div>

      <p className="mt-5 text-xs font-bold uppercase tracking-wide text-gold-600">{s.pickDay}</p>
      <div className="mt-2 flex gap-2 overflow-x-auto pb-2" data-testid="schedule-days">
        {days.map((d, i) => (
          <button
            key={dateKey(d)}
            type="button"
            onClick={() => { setDayIdx(i); setTime(""); }}
            data-testid={`schedule-day-${dateKey(d)}`}
            className={cn(
              "shrink-0 rounded-xl border px-3.5 py-2 text-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500",
              i === dayIdx ? "bg-navy-900 text-white border-navy-900" : "bg-white border-navy-100 text-navy-900 hover:border-gold-500"
            )}
          >
            <span className={cn("block text-[11px] font-bold", i === dayIdx ? "text-gold-400" : "text-charcoal-700/60")}>
              {s.days[d.getDay()]}
            </span>
            <span className="block text-sm font-semibold">{s.months[d.getMonth()]} {d.getDate()}</span>
          </button>
        ))}
      </div>

      <p className="mt-4 text-xs font-bold uppercase tracking-wide text-gold-600">{s.pickTime}</p>
      {allGone ? (
        <p className="mt-2 text-sm text-charcoal-700/70" data-testid="schedule-no-slots">{s.noSlots}</p>
      ) : (
        <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="schedule-times">
          {slots.map((sl) => (
            <button
              key={sl.hm}
              type="button"
              disabled={sl.disabled}
              onClick={() => setTime(sl.hm)}
              data-testid={`schedule-time-${sl.hm}`}
              className={cn(
                "rounded-xl border px-2 py-2.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500",
                sl.disabled
                  ? "bg-navy-50 border-navy-100 text-charcoal-700/30 cursor-not-allowed line-through"
                  : time === sl.hm
                    ? "bg-gold-500 border-gold-500 text-navy-900"
                    : "bg-white border-navy-100 text-navy-900 hover:border-gold-500"
              )}
            >
              {fmtTime(sl.hm)}
            </button>
          ))}
        </div>
      )}

      <p className="mt-3 text-xs text-charcoal-700/60">{s.tz}</p>

      <div className="mt-4 flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={confirm}
          disabled={!time || submitting}
          data-testid="schedule-confirm"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-navy-900 text-white font-semibold px-6 py-3 hover:bg-navy-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
        >
          {submitting ? <><Loader2 size={17} className="animate-spin" aria-hidden /> {s.confirming}</> : s.confirm}
        </button>
        <button
          type="button"
          onClick={() => setSkipped(true)}
          data-testid="schedule-skip"
          className="text-sm font-semibold text-charcoal-700/70 hover:text-navy-900 transition-colors underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 rounded-md px-2 py-1"
        >
          {s.skip}
        </button>
      </div>
    </div>
  );
};
